import { useTranslation } from 'react-i18next';
import {
  Button, generateTableFromSchema, Message, Modal, Section, Skeleton, Switch, TableSkeleton, Tag,
} from '@zolteam/zol-admin-ui';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import i18n from '../../../i18n';
// Hooks
import { useGetBookingQuery } from '../../../services/bookings/bookingsQueries';
import { useGetBookingDocumentListQuery } from '../../../services/bookingDocuments/bookingDocumentsQueries';
// styles
import styles from './bookingDetails.module.css';
// utils
import { getErrorMessage } from '../../../utils/errors/displayError';
// Domain
import { getBookingDocTypeTrad } from '../../../domain/bookingDocuments/bookingDocumentsUseCases';
import {
  displayCustomerFullName, getTotalPassengers, goToClientDetails, goToEditBooking,
} from '../../../domain/bookings/bookingsUseCases';
import { capitalizedValue } from '../../../domain/flightBrief/flightBriefUseCases';
// Icons
// eslint-disable-next-line import/no-unresolved
import DownloadIcon from '../../../assets/icons/download.svg?react';
// Services
import { toggleDocumentVisibility, detachBookingDocument } from '../../../services/bookingDocuments/bookingDocumentsService';
import { deleteBooking } from '../../../services/bookings/bookingsService';

// components
import FlightBriefDetails from '../flightBriefDetails/FlightBriefDetails';

const FlightSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'BookingFlight',
  properties: {
    type: {
      type: 'string',
      description: ' ',
    },
    airport: {
      type: 'string',
      description: i18n.t('booking.fields.airport'),
    },
    city: {
      type: 'string',
      description: i18n.t('booking.fields.city'),
    },
    date: {
      type: 'string',
      description: i18n.t('booking.fields.date'),
    },
    time: {
      type: 'string',
      description: i18n.t('booking.fields.time'),
    },
    utc: {
      type: 'string',
      description: i18n.t('booking.fields.utc'),
    },
  },
  additionalProperties: false,
};

const PassengerSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'BookingPassenger',
  properties: {
    type: {
      type: 'string',
      description: i18n.t('booking.fields.passengers.type'),
    },
    total: {
      type: 'string',
      description: i18n.t('booking.fields.passengers.total'),
    },
  },
  additionalProperties: false,
};

const LuggageSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'BookingLuggage',
  properties: {
    type: {
      type: 'string',
      description: i18n.t('booking.fields.luggage.type'),
    },
    total: {
      type: 'string',
      description: i18n.t('booking.fields.luggage.total'),
    },
  },
  additionalProperties: false,
};

const RequestSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'BookingRequest',
  properties: {
    type: {
      type: 'string',
      description: i18n.t('booking.fields.requests.type'),
    },
  },
  additionalProperties: false,
};

const DocumentSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'BookingDocument',
  properties: {
    tradType: {
      type: 'string',
      description: i18n.t('booking.fields.documents.type'),
    },
    title: {
      type: 'string',
      description: i18n.t('booking.fields.documents.title'),
    },
  },
  additionalProperties: false,
};

/**
 * Booking details component form for panel
 * @param {object} props
 * @param {string} props.bookingId
 * @param {() => void} [props.handleClosePanel]
 * @returns {JSX.Element}
 */
function BookingDetails({ bookingId, handleClosePanel = () => {} }) {
  // local states
  const [isDocumentVisibilitModalOpen, setIsDocumentVisibilitModalOpen] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isLanguageDownloadModalOpen, setIsLanguageDownloadModalOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [showFlightBrief, setShowFlightBrief] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetachModalDocumentOpen, setIsDetachModalDocumentOpen] = useState(false);

  // hooks
  const { t } = useTranslation();

  // mutations
  const { mutate: toggleVisibilityMutate } = useMutation(toggleDocumentVisibility);
  const {
    mutate: deleteBookingMutate,
    isLoading: isLoadingDeleteBooking,
    error: deleteBookingError,
    isError: isErrorDeleteBooking,
  } = useMutation(() => deleteBooking(bookingId), {
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      setCurrentDocument(null);
      handleClosePanel();
    },
  });

  // queries
  const {
    data: dataBooking,
    isLoading: isLoadingDataBooking,
    isError: isGetBookingError,
    error: getBookingError,
  } = useGetBookingQuery(
    bookingId,
    { enabled: !!bookingId },
  );

  const {
    data: bookingDocuments,
    isLoading: isLoadingBookingDocuments,
    isError: isGetBookingDocumentsError,
    error: getBookingDocumentsError,
    refetch: refetchBookingDocuments,
  } = useGetBookingDocumentListQuery(
    { queryParams: { pageSize: 100, pageNumber: 1 }, bookingId },
    { enabled: !!bookingId },
  );

  const formattedBookingDocumentData = useMemo(() => (bookingDocuments?.data?.map(
    (item) => Object.assign(item, {
      tradType: getBookingDocTypeTrad(item.type),
      linkedBookingNb: item.bookings?.length,
    }),
  )), [bookingDocuments?.data]);

  /**
   * @param {object} param
   * @param {number} param.documentId
   * @param {number[]} param.bookingIds
   * @returns {void}
   */
  const {
    mutate: detachBookingDocumentMutate,
    isLoading: isLoadingDetachBookingDocument,
    error: detachBookingDocumentError,
    isError: isErrorDetachBookingDocument,
  } = useMutation(detachBookingDocument, {
    onSuccess: () => {
      setIsDetachModalDocumentOpen(false);
      setCurrentDocument(null);
      refetchBookingDocuments();
    },
  });

  const handleGoToEdit = () => {
    goToEditBooking(bookingId);
  };

  const handleGoToFrenchSharedBrief = () => {
    window.open(`/shared-flight-brief/${dataBooking?.flightBriefUuid}?lng=fr`, '_blank');
  };

  const handleGoToEnglishSharedBrief = () => {
    window.open(`/shared-flight-brief/${dataBooking?.flightBriefUuid}?lng=en`, '_blank');
  };

  const handleGoToSharedBrief = () => {
    setIsLanguageModalOpen(true);
  };

  const handleGoDownloadSharedBriefFrench = () => {
    window.open(`/shared-flight-brief/${dataBooking?.flightBriefUuid}?download&lng=fr`, '_blank');
  };

  const handleGoDownloadSharedBriefEnglish = () => {
    window.open(`/shared-flight-brief/${dataBooking?.flightBriefUuid}?download&lng=en`, '_blank');
  };

  const handleGoDownloadSharedBrief = () => {
    setIsLanguageDownloadModalOpen(true);
  };

  /**
   * @param {object} param
   * @param {boolean} param.isVisible
   * @param {number[]} param.bookingIds
   * @param {string} param.fileUuid
   * @returns {void}
   */
  const handleToggleVisibility = ({ isVisible, bookingIds, fileUuid }) => {
    toggleVisibilityMutate({
      newVisibility: { bookingIds, isVisible },
      documentUuid: fileUuid,
    }, {
      onSuccess: () => {
        setIsDocumentVisibilitModalOpen(false);
        setCurrentDocument(null);
        refetchBookingDocuments();
      },
    });
  };

  /**
   * @param {BookingDocument} [document]
   */
  const handleOpenVisibilityModal = (document) => {
    setCurrentDocument(document);
    if (document.bookings.length === 1) {
      handleToggleVisibility({
        isVisible: !document.isVisible,
        bookingIds: document.bookings,
        fileUuid: document.fileUuid,
      });
    } else {
      setIsDocumentVisibilitModalOpen(true);
    }
  };

  const handleConfirmDelete = () => {
    deleteBookingMutate();
  };

  const handleConfirmDetachDocument = () => {
    const bookingIdNumber = parseInt(bookingId, 10);
    detachBookingDocumentMutate({
      documentId: currentDocument.id,
      bookingIds: [bookingIdNumber],
    });
  };

  /**
   * @param {number[]} bookingIds
   */
  const handleConfirmDetachAllDocuments = (bookingIds) => {
    detachBookingDocumentMutate({
      documentId: currentDocument.id,
      bookingIds,
    });
  };

  return (
    <div className="d-flex f-column justify-between full-screen-height">
      <div>
        {isGetBookingError ? (
          <div className="margin-bottom-m">
            <Message>
              {getErrorMessage(getBookingError
              // @ts-ignore
                .code)}
            </Message>
          </div>
        ) : null}
        {isGetBookingDocumentsError ? (
          <div className="margin-bottom-m">
            <Message>
              {getErrorMessage(getBookingDocumentsError
              // @ts-ignore
                .code)}
            </Message>
          </div>
        ) : null}
        <div>
          {isLoadingDataBooking
            ? <Skeleton height="40px" width="40vw" shape="rectangle" className="margin-bottom-m" />
            : (
              <div className="d-flex f-row f-wrap justify-between margin-bottom-m">
                <div className="d-flex f-row gap-s">
                  <p className="h1-default margin-right-m">{`${dataBooking?.departure.airport.airportCode?.toUpperCase()} - ${dataBooking?.arrival.airport.airportCode?.toUpperCase()}`}</p>
                  <Tag size="medium" label={t(`bookingStatus.${dataBooking?.status}`)} />
                </div>
                <div className="d-flex f-row gap-s self-end">
                  {dataBooking.flightBriefUuid ? (
                    <Button
                      onClick={() => setShowFlightBrief(!showFlightBrief)}
                      color="noBackground"
                    >
                      {showFlightBrief ? t('flightBrief.actions.view') : t('flightBrief.actions.viewBrief')}
                    </Button>
                  ) : null}
                  {showFlightBrief ? (
                    <Button
                      color="primary"
                      icon="eye"
                      onClick={handleGoToSharedBrief}
                    />
                  ) : null}
                  {showFlightBrief ? (
                    <Button
                      color="primary"
                      onClick={handleGoDownloadSharedBrief}
                      className={styles['custom-button']}
                    >
                      <DownloadIcon />
                    </Button>
                  ) : null}
                  <Button
                    onClick={handleGoToEdit}
                    icon="edit"
                    isLoading={isLoadingDataBooking}
                  />
                  <Button
                    color="alert"
                    icon="trash"
                    onClick={() => setIsDeleteModalOpen(true)}
                    isLoading={isLoadingDeleteBooking}
                  />
                </div>
              </div>
            )}
          {showFlightBrief ? (
            <FlightBriefDetails
              uuid={dataBooking?.flightBriefUuid}
              customerId={dataBooking?.customer?.id}
              passengerTotal={getTotalPassengers(dataBooking?.passengers)}
            />
          ) : (
            <div className="d-flex f-column gap-l">
              <Section
                id="customer-details"
                title=""
                isDisplay
              >
                {isLoadingDataBooking
                  ? <Skeleton height="64px" width="40vw" shape="rectangle" className="margin-bottom-m" />
                  : (
                    <>
                      <p className="p1-default">
                        {t('booking.fields.customer')}
                        {' : '}
                        <a
                          className={`${goToClientDetails(dataBooking?.customer) ? 'underline' : ''}`}
                          href={goToClientDetails(dataBooking?.customer)}
                        >
                          {displayCustomerFullName(dataBooking?.customer)}
                        </a>
                      </p>
                      <p className="p1-default">
                        {t('booking.fields.broker')}
                        {' : '}
                        <span>{`${dataBooking?.broker?.firstname || ''} ${dataBooking?.broker?.lastname || ''}`}</span>
                      </p>
                      <p className="p1-default">
                        {t('booking.fields.createdAt')}
                        {' : '}
                        <span>{`${format(new Date(dataBooking?.createdAt), 'dd/MM/yyyy')}`}</span>
                      </p>
                    </>
                  )}
              </Section>

              <Section
                id="flight-details"
                title={t('booking.section.flight')}
                isDisplay
              >
                {
                  isLoadingDataBooking
                    ? <TableSkeleton />
                    : (
                      <div className={styles['large-table']}>
                        {generateTableFromSchema({
                          JSONSchema: FlightSchema,
                          data: [
                            {
                              type: t('booking.fields.departure'),
                              airport: dataBooking?.departure.airport?.airportCode?.toUpperCase(),
                              date: dataBooking?.departure.date ? format(new Date(dataBooking?.departure.date), 'dd/MM/yyyy') : '-',
                              time: dataBooking?.departure.time,
                              city: capitalizedValue(dataBooking?.departure.airport?.city),
                              utc: dataBooking?.departure.utc,
                            },
                            {
                              type: t('booking.fields.arrival'),
                              airport: dataBooking?.arrival.airport?.airportCode?.toUpperCase(),
                              date: dataBooking?.arrival.date ? format(new Date(dataBooking?.arrival.date), 'dd/MM/yyyy') : '-',
                              time: dataBooking?.arrival.time,
                              city: capitalizedValue(dataBooking?.arrival.airport?.city),
                              utc: dataBooking?.arrival.utc,
                            },
                          ],
                        })}
                      </div>
                    )
                }
              </Section>

              <Section
                id="passengers-luggage-details"
                title={t('booking.section.passengersAndLuggage')}
                isDisplay
              >
                <div className="d-flex f-row f-wrap gap-m align-end">
                  <div>
                    <p className="p2-default">
                      {t('booking.fields.passengersNb', {
                        count: getTotalPassengers(dataBooking?.passengers),
                      })}
                    </p>
                    {
                  isLoadingDataBooking
                    ? <TableSkeleton />
                    : (
                      <div className={styles['type-total-table']}>
                        {generateTableFromSchema({
                          JSONSchema: PassengerSchema,
                          data: [
                            {
                              type: t('booking.fields.passengers.adult'),
                              total: dataBooking?.passengers?.adults || 0,
                            },
                            {
                              type: t('booking.fields.passengers.children'),
                              total: dataBooking?.passengers?.children || 0,
                            },
                            {
                              type: t('booking.fields.passengers.infant'),
                              total: dataBooking?.passengers?.infants || 0,
                            },
                            {
                              type: t('booking.fields.passengers.animal'),
                              total: dataBooking?.passengers?.animals || 0,
                            },
                          ],
                        })}
                      </div>
                    )
                }
                  </div>
                  <div>
                    <p className="p2-default">
                      {t('booking.fields.luggageNb', {
                        count: (dataBooking?.luggage?.s || 0)
                          + (dataBooking?.luggage?.m || 0)
                          + (dataBooking?.luggage?.l || 0)
                          + (dataBooking?.luggage?.xl || 0),
                      })}
                    </p>
                    {
                isLoadingDataBooking
                  ? <TableSkeleton />
                  : (
                    <div className={styles['type-total-table']}>
                      {generateTableFromSchema({
                        JSONSchema: LuggageSchema,
                        data: [
                          {
                            type: t('booking.fields.luggage.s'),
                            total: dataBooking?.luggage?.s || 0,
                          },
                          {
                            type: t('booking.fields.luggage.m'),
                            total: dataBooking?.luggage?.m || 0,
                          },
                          {
                            type: t('booking.fields.luggage.l'),
                            total: dataBooking?.luggage?.l || 0,
                          },
                          {
                            type: t('booking.fields.luggage.xl'),
                            total: dataBooking?.luggage?.xl || 0,
                          },
                        ],
                      })}
                    </div>
                  )
                }
                  </div>
                </div>
              </Section>
              <Section
                id="requests-details"
                title={t('booking.section.requests')}
                isDisplay
              >
                {
                  isLoadingDataBooking
                    ? <TableSkeleton />
                    : (
                      <div className={styles['large-table']}>
                        {
                          generateTableFromSchema({
                            JSONSchema: RequestSchema,
                            data: [
                              {
                                type: t('booking.fields.requests.catering'),
                                value: dataBooking?.extra?.catering || '-',
                              },
                              {
                                type: t('booking.fields.requests.transport'),
                                value: dataBooking?.extra?.transport || '-',
                              },
                              {
                                type: t('booking.fields.requests.other'),
                                value: dataBooking?.extra?.other || '-',
                              },
                            ],
                            customColumns: [
                              {
                                key: 'value',
                                description: t('booking.fields.requests.value'),
                                sortable: false,
                                position: 'end',
                                renderCell: (/** @type {{value: string}} */row) => (
                                  <p className="ws-normal">{row.value}</p>
                                ),
                              },
                            ],
                          })
                        }
                      </div>
                    )
                }
              </Section>
              <Section
                id="documents"
                title={t('booking.section.documents')}
                isDisplay
              >
                {
                  isLoadingBookingDocuments
                    ? <TableSkeleton />
                    : (
                      <div className={styles['large-table']}>
                        {isLoadingBookingDocuments ? <TableSkeleton />
                          : generateTableFromSchema({
                            JSONSchema: DocumentSchema,
                            data: formattedBookingDocumentData || [],
                            labelNoResult: t('booking.fields.documents.noResult'),
                            customColumns: [
                              {
                                key: 'linkedBookingNb',
                                description: t('booking.fields.documents.linkedBookingNb'),
                                sortable: false,
                                position: 'end',
                                renderCell: (/** @type {BookingDocument} */row) => (
                                  <a
                                    href={`/bookings?bookingIds=${row.bookings.join(',')}&documentName=${row.title}`}
                                    className="underline cell d-flex full-width"
                                  >
                                    <span className="font-color-primary-500 font-weight-bold">{row.bookings.length}</span>
                                  </a>
                                ),
                              },
                              {
                                key: 'isVisible',
                                description: t('booking.fields.documents.isVisible'),
                                sortable: false,
                                position: 'end',
                                renderCell: (/** @type {BookingDocument} */row) => (
                                  <div className="margin-left-m">
                                    <Switch
                                      id={`isVisible-${row.fileUuid}`}
                                      name={`isVisible-${row.fileUuid}`}
                                      size="small"
                                      checked={row.isVisible}
                                      onChange={() => handleOpenVisibilityModal(row)}
                                    />
                                  </div>
                                ),
                              },
                              {
                                key: 'actions',
                                description: ' ',
                                sortable: false,
                                position: 'end',
                                renderCell: (/** @type {BookingDocument} */row) => (
                                  <div className={`margin-left-m ${styles['action-column']}`}>
                                    <Button
                                      color="noBackground"
                                      icon="eye"
                                      onClick={() => {
                                        if (typeof row.file === 'string') {
                                          window.open(row.file, '_blank');
                                        }
                                      }}
                                    />

                                    <Button
                                      color="noBackground"
                                      icon="trash"
                                      onClick={() => {
                                        setIsDetachModalDocumentOpen(true);
                                        setCurrentDocument(row);
                                      }}
                                    />
                                  </div>
                                ),
                              },
                            ],
                          })}
                      </div>
                    )
                }
              </Section>
              <Section
                id="payment-details"
                title={t('bookingForm.section.payment')}
                isDisplay
              >
                {isLoadingDataBooking
                  ? <Skeleton height="64px" width="40vw" shape="rectangle" className="margin-bottom-m" />
                  : (
                    <>
                      <p className="p1-default">
                        {t('bookingForm.fields.paymentLink')}
                        {' : '}
                        {dataBooking?.paymentLink || '-'}
                      </p>
                      <p className="p1-default">
                        {t('bookingForm.fields.paymentHoldLink')}
                        {' : '}
                        {dataBooking?.paymentHoldLink || '-'}
                      </p>
                    </>
                  )}
              </Section>
              <Section
                id="note"
                title={t('bookingForm.section.note')}
                isDisplay
              >
                {isLoadingDataBooking
                  ? <Skeleton height="64px" width="40vw" shape="rectangle" className="margin-bottom-m" />
                  : (
                    <p className="p1-default">
                      {dataBooking?.note || '-'}
                    </p>
                  )}
              </Section>
            </div>
          )}
        </div>
      </div>
      <div className="gap-m d-flex f-column margin-top-m padding-bottom-m" />
      {isDocumentVisibilitModalOpen ? (
        <Modal
          isOpen={isDocumentVisibilitModalOpen}
          onClose={() => {
            setIsDocumentVisibilitModalOpen(false);
            setCurrentDocument(null);
          }}
          id="document-visibility-modal"
          title={t('booking.documentsVisibilityModal.title')}
          size="medium"
          hasCloseCross
        >
          <p className="p1-default">{t('booking.documentsVisibilityModal.description')}</p>
          <div className="gap-m d-flex f-row f-wrap justify-center margin-top-l">
            <Button
              onClick={() => handleToggleVisibility({
                isVisible: !currentDocument.isVisible,
                bookingIds: currentDocument.bookings,
                fileUuid: currentDocument.fileUuid,
              })}
              isLoading={false}
            >
              <p>{t('booking.documentsVisibilityModal.actions.allBookings')}</p>
            </Button>
            <Button
              color="primary100"
              onClick={() => handleToggleVisibility({
                isVisible: !currentDocument.isVisible,
                bookingIds: [parseInt(bookingId, 10)],
                fileUuid: currentDocument.fileUuid,
              })}
              isLoading={false}
            >
              <p>{t('booking.documentsVisibilityModal.actions.onlyThisBooking')}</p>
            </Button>
          </div>
        </Modal>
      ) : null}

      {isLanguageModalOpen ? (
        <Modal
          isOpen={isLanguageModalOpen}
          onClose={() => setIsLanguageModalOpen(false)}
          id="language-selection-modal"
          title={t('flightBrief.languageModal.title')}
          size="medium"
          hasCloseCross
        >
          <div className="gap-m d-flex f-row f-wrap justify-center margin-top-l">
            <Button
              onClick={handleGoToFrenchSharedBrief}
              isLoading={false}
            >
              <p>{t('flightBrief.languageModal.actions.french')}</p>
            </Button>
            <Button
              color="primary100"
              onClick={handleGoToEnglishSharedBrief}
              isLoading={false}
            >
              <p>{t('flightBrief.languageModal.actions.english')}</p>
            </Button>
          </div>
        </Modal>
      ) : null}

      {isLanguageDownloadModalOpen ? (
        <Modal
          isOpen={isLanguageDownloadModalOpen}
          onClose={() => setIsLanguageDownloadModalOpen(false)}
          id="language-selection-modal"
          title={t('flightBrief.languageDownloadModal.title')}
          size="medium"
          hasCloseCross
        >
          <div className="gap-m d-flex f-row f-wrap justify-center margin-top-l">
            <Button
              onClick={handleGoDownloadSharedBriefFrench}
              isLoading={false}
              disabled={isLoadingDataBooking}
            >
              <p>{t('flightBrief.languageDownloadModal.actions.french')}</p>
            </Button>
            <Button
              color="primary100"
              onClick={handleGoDownloadSharedBriefEnglish}
              isLoading={false}
              disabled={isLoadingDataBooking}
            >
              <p>{t('flightBrief.languageDownloadModal.actions.english')}</p>
            </Button>
          </div>
        </Modal>
      ) : null}

      {isDeleteModalOpen ? (
        <Modal
          id="deleteBookingModal"
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title={t('bookingList.delete.title')}
          size="small"
        >
          {isErrorDeleteBooking ? (
            <Message>
              {t(`apiErrors.${deleteBookingError
              // @ts-ignore
                ?.code || deleteBookingError?.code}`)}
            </Message>
          ) : null}
          <div className="d-flex f-column gap-m">
            <p className="p1-default">
              {t('bookingList.delete.description')}
            </p>
            <div className="d-flex f-row justify-end gap-s">
              <Button
                color="neutral00"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                color="alert"
                onClick={handleConfirmDelete}
                isLoading={isLoadingDeleteBooking}
              >
                {t('common.delete')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {isDetachModalDocumentOpen ? (
        <Modal
          id="deleteBookingDocumentModal"
          isOpen={isDetachModalDocumentOpen}
          onClose={() => setIsDetachModalDocumentOpen(false)}
          title={t('bookingList.deleteDocument.title')}
          size="large"
        >
          {isErrorDetachBookingDocument ? (
            <Message>
              {t(`apiErrors.${detachBookingDocumentError
              // @ts-ignore
                ?.code || detachBookingDocumentError?.code}`)}
            </Message>
          ) : null}
          <p className="p1-default">
            {t('bookingList.deleteDocument.description')}
          </p>
          <div className="d-flex f-row justify-end gap-s margin-top-l">
            <Button
              color="neutral00"
              onClick={() => setIsDetachModalDocumentOpen(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              color="alert"
              onClick={handleConfirmDetachDocument}
              isLoading={isLoadingDetachBookingDocument}
            >
              {t('bookingList.deleteDocument.delete')}
            </Button>
            <Button
              color="alert"
              onClick={() => handleConfirmDetachAllDocuments(currentDocument.bookings)}
              isLoading={isLoadingDetachBookingDocument}
            >
              {t('bookingList.deleteDocument.deleteAll')}
            </Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

export default BookingDetails;
